import Highcharts from 'highcharts';
// Side-effect import: registers the accessibility module (keyboard nav + screen
// reader support). REQUIRED on every chart — Revenant must pass its own WCAG
// dogfood gate, and a chart is always paired with a data-table equivalent by
// the consuming page. (Standing decision: Highcharts is the charting lib.)
import 'highcharts/modules/accessibility';
// REV-803: the palette's CSS variables, as a same-origin stylesheet. Highcharts
// would otherwise inject them into every chart as an inline <style> with a
// hard-coded nonce="highcharts", which the Content-Security-Policy blocks: a
// nonce anyone can read protects nothing. See palette.injectCSS below.
import 'highcharts/css/highcharts-palette.css';
import HighchartsReact from 'highcharts-react-official';
import { useTheme, type Theme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { seriesPalette } from '@utils/chartTokens';

/**
 * Chart chrome derived from the live MUI theme.
 *
 * REV-483: this used to be a hard-coded light palette pinned once at module
 * scope, on the premise that "the Revenant UI is light-only". REV-491 made
 * that false. In dark mode the axis labels rendered #333333 on a #1E1E1E
 * Paper — 1.32:1, effectively invisible.
 *
 * Neither a11y gate caught it. The settings-page dark-mode gate has no charts,
 * and the engine evaluates HTML colour, not SVG `fill` against an ancestor's
 * background, so a chart could be unreadable while the page audited clean.
 * The dashboard spec now measures the rendered contrast directly.
 */
function themeDefaults(theme: Theme): Highcharts.Options {
  const text = theme.palette.text.primary;
  const muted = theme.palette.text.secondary;
  const line = theme.palette.divider;
  return {
    // REV-815: the categorical ramp comes from the shared token table, per
    // theme, per render. It used to be one palette pinned at module scope for
    // both themes, and three of its six colours were under 3:1 on the dark
    // paper — #7B1FA2 measured 2.03:1. Setting it here rather than through
    // Highcharts.setOptions also keeps it overridable: a chart that colours by
    // severity or verdict passes its own token-derived colours and wins the
    // merge.
    //
    // Highcharts 13 would otherwise auto-derive a dark palette from
    // `prefers-color-scheme: dark`, which is still wrong for us: the chart
    // must follow the theme the user pinned in the app, not the OS.
    colors: seriesPalette(theme.palette.mode),
    // REV-803: load the palette CSS from the stylesheet imported above rather
    // than injecting it inline (blocked by the CSP).
    palette: { injectCSS: false },
    chart: { backgroundColor: 'transparent', style: { fontFamily: 'inherit', color: text } },
    xAxis: {
      lineColor: line,
      tickColor: line,
      labels: { style: { color: text } },
      title: { style: { color: muted } },
    },
    yAxis: {
      gridLineColor: line,
      lineColor: line,
      tickColor: line,
      labels: { style: { color: text } },
      title: { style: { color: muted } },
    },
    legend: { itemStyle: { color: text } },
    tooltip: {
      backgroundColor: theme.palette.background.paper,
      style: { color: text },
      borderColor: line,
    },
  };
}

interface HighchartsChartProps {
  options: Highcharts.Options;
  /** Screen-reader description of what the chart conveys. */
  description?: string;
  /**
   * Distinct name for this chart, used only by assistive technology.
   *
   * REV-504: with no chart title, Highcharts' accessibility module names every
   * chart "Chart" — it emits an `<h3>Chart</h3>` in its screen-reader region
   * and labels the chart's region landmark "Chart. Highcharts interactive
   * chart.". One chart per page is fine; the moment a page has two, that
   * produces duplicate headings (STRUCTURE-06) and duplicate landmark names
   * (STRUCTURE-21) — both caught by our own dogfood gate.
   *
   * Pass a distinct name on every chart on a multi-chart page. It is not
   * rendered visually, so it does not duplicate the panel's own heading.
   */
  accessibleName?: string;
}

/**
 * Thin wrapper around HighchartsReact that forces accessible, brand-neutral
 * defaults: the accessibility module enabled, credits off. Consumers always
 * render an accessible data table alongside the chart.
 */
export default function HighchartsChart({
  options,
  description,
  accessibleName,
}: HighchartsChartProps) {
  const { t } = useTranslation();
  const theme = useTheme();
  // Highcharts.merge deep-merges, which matters: callers pass their own
  // `xAxis`/`yAxis` objects, and a shallow spread would drop the themed label
  // colours underneath them — exactly the silent failure this replaced.
  const themed = Highcharts.merge(themeDefaults(theme), options);
  const merged: Highcharts.Options = {
    credits: { enabled: false },
    ...themed,
    accessibility: {
      enabled: true,
      ...(description ? { description } : {}),
      ...(options.accessibility || {}),
    },
    // Names the chart's region landmark so two charts on one page are
    // distinguishable (STRUCTURE-21), and drops the heading Highcharts injects
    // into its screen-reader region.
    //
    // The heading is removed rather than renamed: every chart here already sits
    // beneath a real <h2> naming the same panel, so the injected <h3> either
    // duplicates that text (STRUCTURE-06) or invents a second, competing name
    // for the same thing. The region stays labelled and the data-table
    // equivalent carries the same content.
    ...(accessibleName
      ? {
          lang: {
            ...(options.lang || {}),
            accessibility: {
              ...(options.lang?.accessibility || {}),
              defaultChartTitle: accessibleName,
              // Translated: this is the chart's accessible name, i.e. what a
              // screen-reader user actually hears.
              chartContainerLabel: t('charts.containerLabel', {
                name: accessibleName,
              }),
            },
          },
        }
      : {}),
  };

  if (accessibleName) {
    merged.accessibility = {
      ...merged.accessibility,
      screenReaderSection: {
        ...(merged.accessibility?.screenReaderSection || {}),
        // Highcharts' default format with ONLY the leading
        // <h{n}>{chartTitle}</h{n}> removed. Every other placeholder is kept —
        // dropping {chartSubtitle} or the annotations would silently strip
        // content from the screen-reader region on any chart that uses them,
        // and the two buttons render nothing unless their modules are loaded.
        beforeChartFormat:
          '<div>{typeDescription}</div><div>{chartSubtitle}</div>' +
          '<div>{chartLongdesc}</div><div>{playAsSoundButton}</div>' +
          '<div>{viewTableButton}</div><div>{xAxisDescription}</div>' +
          '<div>{yAxisDescription}</div>' +
          '<div>{annotationsTitle}{annotationsList}</div>',
      },
    };
  }

  return <HighchartsReact highcharts={Highcharts} options={merged} />;
}
